import type { EligibleSubsidy, SubsidiesResponse } from "@/features/subventions/types/subvention.types";
import { firstNameToken, lastNameToken, roleLabel } from "@/features/subventions/utils/subventionStatus";

// A holder as the V2 feed presents it: a short role chip + a display name (the mockup uses the
// employee's first name and a beneficiary's last name).
export type FeedHolder = {
  id: string | number
  fullName: string
  displayName: string
  role: string
  subsidies: EligibleSubsidy[]
};

const EMPLOYEE_ROLE = "Salarié";

// Relationship names are org-configurable free text, so spouse is matched heuristically on the
// common French terms. Used to surface the spouse pill right after the employee.
const SPOUSE_RE = /conjoint|partenaire|[ée]poux|[ée]pouse|concubin|\bpacs|\bmari\b|\bfemme\b/i;
const isSpouse = (relationship: string | null | undefined): boolean => SPOUSE_RE.test(relationship ?? "");

export const buildFeedHolders = (data: SubsidiesResponse | undefined): FeedHolder[] => {
  const subsidies = data?.subsidies;
  if (!subsidies) return [];

  const holders: FeedHolder[] = [];

  const employee = subsidies.employee;
  if (employee.eligible_subsidies.length) {
    holders.push({
      id: employee.id,
      fullName: employee.name,
      displayName: firstNameToken(employee.name),
      role: EMPLOYEE_ROLE,
      subsidies: employee.eligible_subsidies,
    });
  }

  // Spouse(s) come first among beneficiaries (right after the employee), the rest keep API order.
  const beneficiaries = (subsidies.beneficiaries ?? []).filter((b) => b.eligible_subsidies.length);
  const orderedBeneficiaries = [
    ...beneficiaries.filter((b) => isSpouse(b.relationship)),
    ...beneficiaries.filter((b) => !isSpouse(b.relationship)),
  ];

  orderedBeneficiaries.forEach((beneficiary) => {
    holders.push({
      id: beneficiary.id,
      fullName: beneficiary.name,
      displayName: lastNameToken(beneficiary.name),
      role: roleLabel(beneficiary.relationship),
      subsidies: beneficiary.eligible_subsidies,
    });
  });

  return holders;
};

// Used as the drawer's holder context line ("Salarié · Camille MARTIN").
export const holderContextLabel = (holder: FeedHolder): string => `${holder.role} · ${holder.fullName}`;
